import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* =============================================================
   TASK: ReadFunctionFromTableTask
   Cel: Odczyt/wnioskowanie z tabeli wartości funkcji:
   - (A) odczytaj wartość funkcji dla danego argumentu
   - (B) podaj argument, dla którego wartość funkcji wynosi y0
        (jeśli y0 występuje kilka razy -> pytamy o najmniejszy/największy argument)

   Ulepszenia:
   - 5–8 wartości w tabeli
   - argumenty rosnące, ale z losowymi odstępami (1–3)
   - brak "f(x)=..." i brak "dla argumentu x" w poleceniach
   - przy powtórzeniach: "najmniejszy/największy argument" -> jednoznacznie
   - tabela: border w kolorze czcionki (currentColor), kolor dziedziczony
   - PUŁAPKA 50/50:
       * w trybie readValue: liczba x0 pojawia się też wśród wartości (gdzie indziej)
       * w trybie findArgument: liczba y0 pojawia się też wśród argumentów (gdzie indziej)
============================================================= */

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do {
    v = Math.floor(Math.random() * span) + min;
  } while (exclude.has(v));
  return v;
}

function buildXs(len) {
  const start = randInt(-8, 1);
  const xs = [start];
  for (let i = 1; i < len; i++) xs.push(xs[i - 1] + randInt(1, 3));
  return xs;
}

function countOccurrences(arr) {
  const m = new Map();
  for (const v of arr) m.set(v, (m.get(v) || 0) + 1);
  return m;
}

function buildLinearTable() {
  const a = randInt(-4, 4, new Set([0]));
  const b = randInt(-6, 6);
  const len = randInt(5, 8);
  const xs = buildXs(len);
  const ys = xs.map((x) => a * x + b);
  return { xs, ys };
}

function buildQuadraticTable() {
  const a = randInt(-2, 2, new Set([0]));
  const b = randInt(-3, 3);
  const c = randInt(-4, 4);
  const len = randInt(5, 8);
  const xs = buildXs(len);
  const ys = xs.map((x) => a * x * x + b * x + c);
  return { xs, ys };
}

function buildTable() {
  const isQuadratic = Math.random() < 0.35;
  return isQuadratic ? buildQuadraticTable() : buildLinearTable();
}

/**
 * Próbuje wygenerować tabelę spełniającą warunek „pułapki”.
 * - wantTrap=true:
 *     readValue:  istnieje i != idx: ys[i] == x0
 *     findArg:    istnieje j != idx: xs[j] == y0
 * - wantTrap=false: nie narzucamy (może się trafić przypadkiem, ale zwykle nie)
 */
function generateWithTrap(mode, wantTrap, maxTries = 60) {
  for (let t = 0; t < maxTries; t++) {
    const { xs, ys } = buildTable();
    const idx = randInt(0, xs.length - 1);

    if (mode === "readValue") {
      const x0 = xs[idx];
      const answerValue = ys[idx];

      if (!wantTrap) {
        return { xs, ys, idx, x0, answerValue, trap: false };
      }

      // pułapka: x0 pojawia się wśród wartości, ale w innej kolumnie
      const hasTrap = ys.some((y, i) => i !== idx && y === x0);
      if (hasTrap) {
        return { xs, ys, idx, x0, answerValue, trap: true };
      }
    } else {
      // findArgument
      const y0 = ys[idx];

      if (!wantTrap) {
        return { xs, ys, idx, y0, trap: false };
      }

      // pułapka: y0 pojawia się wśród argumentów, ale w innej kolumnie
      const hasTrap = xs.some((x, i) => i !== idx && x === y0);
      if (hasTrap) {
        return { xs, ys, idx, y0, trap: true };
      }
    }
  }

  // fallback: jak się nie udało w maxTries, zwróć bez pułapki
  const { xs, ys } = buildTable();
  const idx = randInt(0, xs.length - 1);
  if (mode === "readValue") return { xs, ys, idx, x0: xs[idx], answerValue: ys[idx], trap: false };
  return { xs, ys, idx, y0: ys[idx], trap: false };
}

export function generateTask() {
  // tryb: częściej odczyt wartości (łatwiejsze)
  const mode = Math.random() < 0.6 ? "readValue" : "findArgument";

  // PUŁAPKA 50/50
  const wantTrap = Math.random() < 0.5;

  const generated = generateWithTrap(mode, wantTrap);
  const { xs, ys, idx } = generated;

  if (mode === "readValue") {
    return {
      type: "ReadFunctionFromTableTask",
      data: {
        mode,
        xs,
        ys,
        x0: generated.x0,
        answerValue: generated.answerValue,
        trap: generated.trap, // tylko informacyjnie (nie musisz nigdzie używać)
      },
    };
  }

  // findArgument: wybieramy y0 i ustalamy jednoznaczność:
  const y0 = generated.y0;
  const allArgs = xs.filter((x, i) => ys[i] === y0);
  const occ = allArgs.length;

  let variant = "any";
  let answerArgument = allArgs[0];

  if (occ >= 2) {
    variant = Math.random() < 0.5 ? "min" : "max";
    answerArgument = variant === "min" ? Math.min(...allArgs) : Math.max(...allArgs);
  }

  return {
    type: "ReadFunctionFromTableTask",
    data: {
      mode,
      xs,
      ys,
      y0,
      variant,
      answerArgument,
      trap: generated.trap, // tylko informacyjnie
    },
  };
}

function Table({ xs, ys }) {
  const tableStyle = {
    borderCollapse: "collapse",
    margin: "12px auto 0",
    color: "inherit",
    fontSize: "clamp(14px, 1.6vw, 18px)",
    lineHeight: 1.2,
  };

  const cellStyle = {
    border: "1px solid currentColor",
    padding: "7px 10px",
    textAlign: "center",
    minWidth: 44,
    color: "inherit",
  };

  const headCellStyle = {
    ...cellStyle,
    fontWeight: 700,
    background: "rgba(120,120,120,0.10)",
  };

  return (
    <table style={tableStyle}>
      <tbody>
        <tr>
          <td style={headCellStyle}>
            <InlineMath math={'x'} />
          </td>
          {xs.map((x) => (
            <td key={`x-${x}`} style={cellStyle}>
              {x}
            </td>
          ))}
        </tr>
        <tr>
          <td style={headCellStyle}>
            <InlineMath math={'f(x)'} />
          </td>
          {ys.map((y, i) => (
            <td key={`y-${i}`} style={cellStyle}>
              {y}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

export function renderTask({ data }) {
  const { mode, xs, ys } = data;

  const instruction = (() => {
    if (mode === "readValue") {
      return (
        <div>
          Odczytaj wartość funkcji dla argumentu{" "}
          <InlineMath math={"" + data.x0} />.
        </div>
      );
    }

    if (data.variant === "min") {
      return (
        <div>
          Podaj <strong>najmniejszy</strong> argument, dla którego wartość funkcji wynosi{" "}
          <InlineMath math={"" + data.y0} />.
        </div>
      );
    }

    if (data.variant === "max") {
      return (
        <div>
          Podaj <strong>największy</strong> argument, dla którego wartość funkcji wynosi{" "}
          <InlineMath math={"" + data.y0} />.
        </div>
      );
    }

    return (
      <div>
        Podaj argument, dla którego wartość funkcji wynosi{" "}
        <InlineMath math={"" + data.y0} />.
      </div>
    );
  })();

  return (
    <div style={{ marginTop: 6 }}>
      <div>Funkcja jest opisana tabelą (góra: argumenty, dół: wartości funkcji):</div>
      <Table xs={xs} ys={ys} />
      <div style={{ marginTop: 10 }}>{instruction}</div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const label = data.mode === "readValue" ? "wartość funkcji" : "argument";

  return (
    <div style={{ marginTop: 10 }}>
      <FractionInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label={label}
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  if (parsed == null || isNaN(parsed)) return false;

  const correct =
    data.mode === "readValue" ? data.answerValue : data.answerArgument;

  return Math.abs(parsed - correct) < 0.001;
}

export function renderAnswer({ data }) {
  if (data.mode === "readValue") {
    return <InlineMath math={`\\text{wartość funkcji} = ${data.answerValue}`} />;
  }
  return <InlineMath math={`\\text{argument} = ${data.answerArgument}`} />;
}

export function renderExplanation({ data }) {
  const { mode, xs, ys } = data;

  if (mode === "readValue") {
    const idx = xs.indexOf(data.x0);
    return (
      <div style={{ textAlign: "left", lineHeight: 1.7 }}>
        <div><strong>Wyjaśnienie.</strong></div>
        <div>
          <strong>Argument</strong> to liczba w pierwszym wierszu tabeli (zwykle oznaczana jako{" "}
          <InlineMath math={'x'} />
          ). <strong>Wartość funkcji</strong> jest w drugim wierszu w tej samej kolumnie.
        </div>
        <div style={{ marginTop: 8 }}>
          <strong>Krok 1.</strong> Znajdź w tabeli argument <InlineMath math={"" + data.x0} /> (górny wiersz).
        </div>
        <div>
          <strong>Krok 2.</strong> W tej samej kolumnie odczytaj liczbę z dolnego wiersza.
        </div>
        <BlockMath math={`\\text{wartość funkcji} = ${ys[idx]}`} />
      </div>
    );
  }

  const indices = ys
    .map((v, i) => (v === data.y0 ? i : -1))
    .filter((i) => i !== -1);

  const args = indices.map((i) => xs[i]);

  const what =
    data.variant === "min"
      ? "najmniejszy"
      : data.variant === "max"
        ? "największy"
        : "odpowiedni";

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Wyjaśnienie.</strong></div>
      <div>
        Szukamy w dolnym wierszu wartości <InlineMath math={"" + data.y0} />, a potem patrzymy,
        jaki argument stoi nad nią w górnym wierszu.
      </div>

      <div style={{ marginTop: 8 }}>
        <strong>Krok 1.</strong> Znajdź w dolnym wierszu wartość <InlineMath math={"" + data.y0} />.
      </div>
      <div>
        <strong>Krok 2.</strong> Odczytaj argument nad tą kolumną (górny wiersz).
      </div>

      {args.length > 1 ? (
        <div style={{ marginTop: 8 }}>
          Ta wartość występuje dla argumentów:{" "}
          {args.map((a, i) => (
            <span key={`${a}-${i}`}>
              <InlineMath math={"" + a} />
              {i < args.length - 1 ? ", " : "."}
            </span>
          ))}
          <div>
            W pytaniu proszono o <strong>{what}</strong> argument, więc odpowiedź to{" "}
            <InlineMath math={"" + data.answerArgument} />.
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          Wartość występuje tylko raz, więc argument to{" "}
          <InlineMath math={"" + data.answerArgument} />.
        </div>
      )}

      <BlockMath math={`\\text{argument} = ${data.answerArgument}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={userAnswer.raw} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
